import * as React from 'react';
import { connect } from 'react-redux';
import { Button, Card, Col, Layout, Row } from 'antd';
import { getCurrency, nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { formatDateForParam } from '../../utils/DataFormatterUtils';
import KPIMetricChart from './KPIMetricChart';
import KPIWorkOrdersChart from './KPIWorkOrdersChart';
import { KPI_COMPARISON_API_SUBSTRINGS } from '../../thunks/kpi_comparison_thunks';
import { withRouter } from 'react-router';
import {
	fetchSupplierFacilitiesIfEverScorecardInPrivateNetworkForBuyer,
	fetchSupplierFacilitiesIfEverScorecardInPrivateNetworkForSupplier,
	supplierFacilitiesRestCrudThunksForBuyer,
	supplierFacilitiesRestCrudThunksForSupplier,
} from '../../thunks/supplier_facilities_thunks';
import AnalyticsControlBar from '../analytics_control_bar/AnalyticsControlBar';
import { clearKPIVendorFilters, updateKPIVendorFilters } from '../../actions/kpi_analytics_actions';
import {
	fetchAllKPIs,
	fetchCheckInComplianceKPIByMonth,
	fetchFirstTimeResolutionKPIByMonth,
	fetchOnTimeArrivalKPIByMonth,
	fetchTimeToAcceptKPIByMonth,
	fetchTimeToScheduleKPIByMonth,
	fetchTimelyInvoicingKPIByMonth,
	fetchTimelyWorkCompletionKPIByMonth,
	fetchWorkOrdersKPIByMonth,
} from '../../thunks/kpi_analytics_thunks';
import { debounce } from '../../utils/PerformanceUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { generateBackLink, getBackLinkStates } from '../../utils/HistoryUtils';
import { clearNavbarState, updateNavbarState } from '../../actions/navbar_actions';
import { exportAnalyticsPageAsPdf } from '../../utils/AnalyticsPdfExportUtils';

const queryString = require('qs');

const style = require('./KPIAnalyticsVendorDetailPage.less');
const isEqual = require('fast-deep-equal');

const Content = Layout.Content;

class KPIAnalyticsVendorDetailPage extends React.Component<any, any> {
	state = {
		initialLoading: true,
		workOrderData: {},
	};

	debouncedCallback = debounce(
		() => {
			const {
				kpiAnalytics,
				getWorkOrdersKPIByMonth,
				getCheckInComplianceKPIByMonth,
				getFirstTimeResolutionKPIByMonth,
				getOnTimeArrivalKPIByMonth,
				getTimelyInvoicingKPIByMonth,
				getTimelyWorkCompletionKPIByMonth,
				getTimeToAcceptKPIByMonth,
				getTimeToScheduleKPIByMonth,
				fetchSupplierWoData,
				getKPIs,
			} = this.props;
			const { initialLoading } = this.state;
			const { kpiVendorFilters } = kpiAnalytics;
			const startDate =
				kpiVendorFilters.startDate && formatDateForParam(kpiVendorFilters.startDate);
			const endDate = kpiVendorFilters.endDate && formatDateForParam(kpiVendorFilters.endDate);
			const filters = { ...kpiVendorFilters, startDate, endDate };
			const promises = [
				fetchSupplierWoData(filters),
				getWorkOrdersKPIByMonth(filters),
				getCheckInComplianceKPIByMonth(filters),
				getTimeToAcceptKPIByMonth(filters),
				getTimeToScheduleKPIByMonth(filters),
				getFirstTimeResolutionKPIByMonth(filters),
				getOnTimeArrivalKPIByMonth(filters),
				getTimelyInvoicingKPIByMonth(filters),
				getTimelyWorkCompletionKPIByMonth(filters),
				getKPIs(filters),
			];

			Promise.allSettled(promises).then((data) => {
				this.setState({
					workOrderData: nullSafeGetOrElse('0.value.0', data, {}),
				});

				this.setState({ initialLoading: false });
			});
		},
		500,
		false
	);

	handlePrint = () =>
		exportAnalyticsPageAsPdf(
			'.kpiAnalyticsVendorDetailPage',
			'kpi-vendor-detail-' + new Date().toISOString().slice(0, 10) + '.pdf'
		);

	componentDidUpdate(prevProps) {
		const { kpiAnalytics } = this.props;
		if (!isEqual(prevProps.kpiAnalytics.kpiVendorFilters, kpiAnalytics.kpiVendorFilters)) {
			this.debouncedCallback();
		}
	}

	componentDidMount() {
		const { fetchSupplierFacility, match, saveKPIVendorFilters, location, updateNavbar } =
			this.props;
		const supplierFacilityId = match.params.id;
		fetchSupplierFacility(supplierFacilityId);

		let searchString = location.search;
		if (searchString[0] === '?') {
			searchString = searchString.slice(1);
		}
		const queryParams = queryString.parse(searchString);
		saveKPIVendorFilters({
			supplierFacilityIds: [parseInt(supplierFacilityId, 10)],
			startDate: moment().subtract(6, 'months'),
			endDate: moment(),
			problemTypeIds: queryParams.problemTypeIds,
		});
		const backLinkStates = getBackLinkStates(
			location,
			`/buyer/analytics/kpi/overview/vendors`
		) as any;
		updateNavbar(
			backLinkStates.backLinkText || `Back to Vendors`,
			backLinkStates.backLinkTo ||
				generateBackLink(`/buyer/analytics/kpi/overview/vendors`, location)
		);
	}

	componentWillUnmount() {
		const { clearAllKPIVendorFilters, clearNavbar } = this.props;
		clearAllKPIVendorFilters();
		clearNavbar();
	}

	// Charts feed these filters into `useAnalyticsBreakdownComparison`, whose effect reloads
	// whenever the `filters` reference changes. Building a fresh object every render would retrigger
	// the comparison fetch on each re-render (an endless refetch loop while comparison is on), so we
	// return the same reference until the underlying filter values actually change.
	memoizedChartFilters: Record<string, any> = null;

	getChartFilters(kpiVendorFilters) {
		const startDate = kpiVendorFilters.startDate && formatDateForParam(kpiVendorFilters.startDate);
		const endDate = kpiVendorFilters.endDate && formatDateForParam(kpiVendorFilters.endDate);
		const nextFilters = { ...kpiVendorFilters, startDate, endDate };
		if (this.memoizedChartFilters && isEqual(this.memoizedChartFilters, nextFilters)) {
			return this.memoizedChartFilters;
		}
		this.memoizedChartFilters = nextFilters;
		return nextFilters;
	}

	render() {
		const {
			supplierFacility,
			saveKPIVendorFilters,
			companyConfig,
			match,
			clearAllKPIVendorFilters,
			kpiAnalytics,
			currentUser,
			userType,
			history,
			location,
		} = this.props;
		const { initialLoading } = this.state;
		const {
			workOrdersByMonth,
			timelyWorkCompletionByMonth,
			onTimeArrivalByMonth,
			timeToAcceptByMonth,
			timeToScheduleByMonth,
			checkInComplianceByMonth,
			firstTimeResolutionByMonth,
			timelyInvoicingByMonth,
		} = kpiAnalytics;
		const getKPIColor = (val, baseline) => {
			if (val === null || val === undefined) {
				return 'rgba(0,0,0,0.45)';
			} else if (val >= baseline) {
				return 'green';
			} else {
				return 'red';
			}
		};
		const currency = getCurrency({ currentUser });
		const supplierFacilityId = parseInt(match.params.id, 10);
		const timeMetrics = [
			{
				name: 'Time To Accept',
				data: timeToAcceptByMonth.data,
				loading: timeToAcceptByMonth.loading,
				baseline: 0.95,
				key: 'monthlyTimeToAcceptKPIs.monthlyAverage',
				comparisonApiSubstring: KPI_COMPARISON_API_SUBSTRINGS.timeToAccept,
				chartKey: 'time_to_accept',
			},
			{
				name: 'Time To Schedule Visit',
				data: timeToScheduleByMonth.data,
				loading: timeToScheduleByMonth.loading,
				baseline: 0.95,
				key: 'monthlyTimeToScheduleVisitKPIs.monthlyAverage',
				comparisonApiSubstring: KPI_COMPARISON_API_SUBSTRINGS.timeToSchedule,
				chartKey: 'time_to_schedule',
			},
		];

		const metrics = [
			{
				name: 'Arrived On Time',
				data: onTimeArrivalByMonth.data,
				loading: onTimeArrivalByMonth.loading,
				baseline: 0,
				key: 'monthlyOnTimeArrivalKPIs.monthlyAverage',
				comparisonApiSubstring: KPI_COMPARISON_API_SUBSTRINGS.onTimeArrival,
				chartKey: 'on_time_arrival',
			},
			{
				name: 'Checked In / Out',
				data: checkInComplianceByMonth.data,
				loading: checkInComplianceByMonth.loading,
				baseline: 0,
				key: 'monthlyCheckInKPIs.monthlyAverage',
				comparisonApiSubstring: KPI_COMPARISON_API_SUBSTRINGS.checkInCompliance,
				chartKey: 'check_in_compliance',
			},
			{
				name: 'Completed On Time',
				data: timelyWorkCompletionByMonth.data,
				loading: timelyWorkCompletionByMonth.loading,
				baseline: 0,
				key: 'monthlyWorkCompleteWithinDueDateKPIs.monthlyAverage',
				comparisonApiSubstring: KPI_COMPARISON_API_SUBSTRINGS.timelyWorkCompletion,
				chartKey: 'timely_work_completion',
			},
			{
				name: 'Resolved Issue First Time',
				data: firstTimeResolutionByMonth.data,
				loading: firstTimeResolutionByMonth.loading,
				baseline: 0,
				key: 'monthlyResolvedFirstTimeKPIs.monthlyAverage',
				comparisonApiSubstring: KPI_COMPARISON_API_SUBSTRINGS.firstTimeResolution,
				chartKey: 'first_time_resolution',
			},
			{
				name: 'Invoiced Within 30 Days',
				data: timelyInvoicingByMonth.data,
				loading: timelyInvoicingByMonth.loading,
				baseline: 0,
				key: 'monthlyInvoicedIn30DaysKPIs.monthlyAverage',
				comparisonApiSubstring: KPI_COMPARISON_API_SUBSTRINGS.timelyInvoicing,
				chartKey: 'timely_invoicing',
			},
		];

		const chartFilters = this.getChartFilters(kpiAnalytics.kpiVendorFilters);
		const { workOrderData } = this.state;
		return (
			<Content className="kpiAnalyticsVendorDetailPage" style={{ padding: '0 0.5em' }}>
				<div
					style={{
						margin: '0 -8px',
						padding: '24px',
						paddingBottom: 0,
						background: nullSafeGet('detail.config.theme.navblock', companyConfig),
						marginBottom: 8,
					}}
				>
					<h4
						style={{
							color: 'rgba(255, 255, 255, 0.9)',
							marginRight: 8,
							paddingBottom: 24,
						}}
					>
						{supplierFacility.displayName}
					</h4>
				</div>
				<Row style={{ margin: '16px 4px' }} gutter={16}>
					<AnalyticsControlBar
						updateFilters={(filters = {}) => {
							saveKPIVendorFilters({ ...filters, supplierFacilityIds: [supplierFacilityId] });
						}}
						locationIds={kpiAnalytics.kpiVendorFilters.locationIds}
						startDate={kpiAnalytics.kpiVendorFilters.startDate}
						endDate={kpiAnalytics.kpiVendorFilters.endDate}
						hideSuppliersControl
						filterConfig={{ problemTypes: true, capEx: false }}
						problemTypeIds={kpiAnalytics.kpiVendorFilters.problemTypeIds}
						dateRangeLabel="Date Range"
						showPrint={true}
						onPrint={this.handlePrint}
					/>
				</Row>

				<Row style={{ margin: '16px 0px 0px 0px' }} gutter={16}>
					<Col span={12}>
						<Card
							loading={initialLoading}
							title="completed work orders"
							actions={[
								<Link
									target="_blank"
									to={`/${userType}/workOrders/overview/all?current=1&pageSize=5&sort_by=createdAt&order=desc&displayStatuses=Completed&supplierFacilityIds=${supplierFacilityId}`}
								>
									<Button type="link">
										See details <i className="icons8-font icons8-external-link ml-2 inline-block" />
									</Button>
								</Link>,
							]}
							extra={
								<div className="text-base text-gray-500">
									LIFETIME: {workOrderData.lifeTimeCompletedWorkOrders}
								</div>
							}
						>
							<div className="flex h-40 flex-col items-center justify-center">
								<div className="text-center text-8xl font-normal ">
									{workOrderData.dateRangeCompletedWorkOrders}
								</div>
							</div>
						</Card>
					</Col>
					<Col span={12}>
						<Card
							loading={initialLoading}
							title="Open work orders"
							actions={[
								<Link
									target="_blank"
									to={`/${userType}/workOrders/overview/all?current=1&pageSize=5&sort_by=createdAt&order=desc&displayStatuses=Open%2CInProgress%2COnHold&supplierFacilityIds=${supplierFacilityId}`}
								>
									<Button type="link">
										See details <i className="icons8-font icons8-external-link ml-2 inline-block" />
									</Button>
								</Link>,
							]}
							extra={
								<div className="text-base text-gray-500">
									LIFETIME: {workOrderData.lifeTimeOpenWorkOrders}
								</div>
							}
						>
							<div className="flex h-40 flex-col items-center justify-center">
								<div className="text-center text-8xl font-normal ">
									{workOrderData.dateRangeOpenWorkOrders}
								</div>
							</div>
						</Card>
					</Col>
				</Row>
				<Row style={{ margin: '16px 0px 0px 0px' }} gutter={16}>
					<Col span={12} style={{ marginBottom: 16 }}>
						<KPIWorkOrdersChart
							data={workOrdersByMonth.data || []}
							loading={!!workOrdersByMonth.loading || initialLoading}
							filters={chartFilters}
							userType={userType}
							currency={currency}
							totalWOs={nullSafeGet(
								'monthlyKPIsBySupplier.monthlyWorkOrderKPIs.totalWOs',
								kpiAnalytics
							)}
							averageWOCost={nullSafeGet(
								'monthlyKPIsBySupplier.monthlyWorkOrderKPIs.averageWOCost',
								kpiAnalytics
							)}
						/>
					</Col>
					{timeMetrics.map((metric) => (
						<Col key={metric.chartKey} span={12} style={{ marginBottom: 16 }}>
							<KPIMetricChart
								chartKey={metric.chartKey}
								label={metric.name}
								summaryValue={nullSafeGetOrElse(
									`monthlyKPIsBySupplier.${metric.key}`,
									kpiAnalytics,
									null
								)}
								data={metric.data || []}
								loading={!!metric.loading || initialLoading}
								isTimeBased={true}
								baseline={metric.baseline}
								comparisonApiSubstring={metric.comparisonApiSubstring}
								filters={chartFilters}
								userType={userType}
							/>
						</Col>
					))}
					{metrics.map((metric) => (
						<Col key={metric.chartKey} span={12} style={{ marginBottom: 16 }}>
							<KPIMetricChart
								chartKey={metric.chartKey}
								label={metric.name}
								summaryValue={nullSafeGetOrElse(
									`monthlyKPIsBySupplier.${metric.key}`,
									kpiAnalytics,
									null
								)}
								data={metric.data || []}
								loading={!!metric.loading || initialLoading}
								isTimeBased={false}
								baseline={metric.baseline}
								comparisonApiSubstring={metric.comparisonApiSubstring}
								filters={chartFilters}
								userType={userType}
							/>
						</Col>
					))}
				</Row>
			</Content>
		);
	}
}

const mapStateToProps = (state, ownProps) => ({
	supplierFacilitiesFetching: state.supplier_facilities.fetching,
	supplierFacility: state.supplier_facilities.detail,
	companyConfig: state.company_config,
	match: ownProps.match,
	history: ownProps.history,
	kpiAnalytics: state.kpi_analytics,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	fetchSupplierFacility: (id) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? supplierFacilitiesRestCrudThunksForSupplier.readOne(id)
				: supplierFacilitiesRestCrudThunksForBuyer.readOne(id)
		),
	saveKPIVendorFilters: (filters) => dispatch(updateKPIVendorFilters(filters)),
	clearAllKPIVendorFilters: () => dispatch(clearKPIVendorFilters()),
	getWorkOrdersKPIByMonth: (params) =>
		dispatch(fetchWorkOrdersKPIByMonth(ownProps.userType)(params)),
	getCheckInComplianceKPIByMonth: (params) =>
		dispatch(fetchCheckInComplianceKPIByMonth(ownProps.userType)(params)),
	getTimeToAcceptKPIByMonth: (params) =>
		dispatch(fetchTimeToAcceptKPIByMonth(ownProps.userType)(params)),
	getTimeToScheduleKPIByMonth: (params) =>
		dispatch(fetchTimeToScheduleKPIByMonth(ownProps.userType)(params)),
	getFirstTimeResolutionKPIByMonth: (params) =>
		dispatch(fetchFirstTimeResolutionKPIByMonth(ownProps.userType)(params)),
	getOnTimeArrivalKPIByMonth: (params) =>
		dispatch(fetchOnTimeArrivalKPIByMonth(ownProps.userType)(params)),
	getTimelyInvoicingKPIByMonth: (params) =>
		dispatch(fetchTimelyInvoicingKPIByMonth(ownProps.userType)(params)),
	getTimelyWorkCompletionKPIByMonth: (params) =>
		dispatch(fetchTimelyWorkCompletionKPIByMonth(ownProps.userType)(params)),
	getKPIs: (params, targetCollectionName) =>
		dispatch(fetchAllKPIs(ownProps.userType)(params, targetCollectionName)),
	fetchSupplierWoData: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? fetchSupplierFacilitiesIfEverScorecardInPrivateNetworkForSupplier(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
				: fetchSupplierFacilitiesIfEverScorecardInPrivateNetworkForBuyer(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
		),
	updateNavbar: (backlinkTitle, backlinkTo, navbarBackgroundColor) =>
		dispatch(updateNavbarState(backlinkTitle, backlinkTo, navbarBackgroundColor)),
	clearNavbar: () => dispatch(clearNavbarState()),
});

const ComponentWithoutUserType = withRouter(
	connect(mapStateToProps, mapDispatchToProps)(KPIAnalyticsVendorDetailPage)
);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(ComponentWithoutUserType);
